import React, { useEffect, useRef, useCallback, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

function MobilePdf({ url }) {
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    let pdf;
    const container = containerRef.current;

    (async () => {
      try {
        pdf = await pdfjsLib.getDocument({ url }).promise;
        const cssWidth = container.clientWidth;
        const dpr = window.devicePixelRatio || 1;

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = cssWidth / base.width;
          const viewport = page.getViewport({ scale: scale * dpr });

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${cssWidth}px`;
          canvas.style.height = `${viewport.height / dpr}px`;
          canvas.style.display = "block";
          canvas.style.marginBottom = "8px";
          container.appendChild(canvas);

          await page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
          }).promise;
        }
        if (!cancelled) setStatus("done");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      pdf?.destroy();
      if (container) container.innerHTML = "";
    };
  }, [url]);

  return (
    <>
      {status === "loading" && <div>Loading PDF…</div>}
      {status === "error" && <div>Failed to load PDF.</div>}
      <div ref={containerRef} style={{ width: "100%" }} />
    </>
  );
}

const PDFViewer = ({ url, hideToolbar = false }) => {
  const iframeRef = useRef(null);

  const handlePrint = useCallback(() => {
    try {
      const w = iframeRef.current?.contentWindow;
      w.focus();
      w.print();
    } catch {
      window.open(url, "_blank");
    }
  }, [url]);

  useEffect(() => {
    if (hideToolbar) return;
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hideToolbar, handlePrint]);

  if (hideToolbar) {
    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <button onClick={() => window.open(url, "_blank")}>Open in new tab</button>
          <a href={url} download="document.pdf"><button>Download</button></a>
        </div>
        <MobilePdf key={url} url={url} />
      </div>
    );
  }

   return (
    <iframe
      ref={iframeRef}
      key={url}
      src={`${url}#view=FitH&zoom=page-width`}
      width="100%"
      style={{
        display: "block",
        border: "none",
        height: "calc(100vh - 160px)", 
        minHeight: 600,
      }}
      title="PDF Viewer"
    />
  );
};

export default PDFViewer;